import { matchRoutePattern } from '@/ws/fallback/matchRoutePattern';
import { IdentitySyncOperationType, WsIdentitySyncFailedMessageContent } from '@/ws';
import { UserProfileType } from '@/api/apis/user';
import { usersQueryKeys } from '@/queries/user/user.keys';
import { queryClient } from '@/queries';
import { IdentitySyncRouteConfig } from './identitySyncRouteConfig.types';

export type IdentitySyncRelevance = { isRelevant: true; invalidate: () => Promise<unknown> } | { isRelevant: false };

const isOwnProfileMessage = (message: WsIdentitySyncFailedMessageContent, profile?: UserProfileType): boolean => {
  switch (message.operationType) {
    case IdentitySyncOperationType.EmployeeModified:
    case IdentitySyncOperationType.EmployeeEnabled:
    case IdentitySyncOperationType.EmployeeDisabled:
      return !!message.entityId && message.entityId === profile?.employee?.id;
    case IdentitySyncOperationType.CustomerModified:
    case IdentitySyncOperationType.CustomerEnabled:
    case IdentitySyncOperationType.CustomerDisabled:
      return !!message.entityId && message.entityId === profile?.customer?.id;
    case IdentitySyncOperationType.OpoUserDataUpdated:
      // entityId is not available for this operation - the contact owner is identified via targetUserId instead.
      return !!message.targetUserId && message.targetUserId === profile?.userId;
    default:
      return false;
  }
};

export const resolveIdentitySyncRelevance = (
  message: WsIdentitySyncFailedMessageContent,
  pathname: string,
  profile: UserProfileType | undefined,
  config: IdentitySyncRouteConfig,
): IdentitySyncRelevance => {
  if (isOwnProfileMessage(message, profile)) {
    return {
      isRelevant: true,
      invalidate: () => queryClient.invalidateQueries({ queryKey: usersQueryKeys.getProfile() }),
    };
  }

  const detailMatch = matchRoutePattern(config.detailRoutes, pathname);
  if (detailMatch && detailMatch.entry.operationTypes.includes(message.operationType)) {
    const viewedEntityId = detailMatch.entry.resolveViewedEntityId(detailMatch.params);
    const messageEntityId = message.entityId ?? message.targetUserId;

    if (viewedEntityId && messageEntityId && viewedEntityId === messageEntityId) {
      return { isRelevant: true, invalidate: () => detailMatch.entry.invalidate(detailMatch.params) };
    }
    return { isRelevant: false };
  }

  const listMatch = matchRoutePattern(config.listRoutes, pathname);
  if (listMatch && listMatch.entry.operationTypes.includes(message.operationType)) {
    return { isRelevant: true, invalidate: () => listMatch.entry.invalidate() };
  }

  return { isRelevant: false };
};
